import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Auto-scrolling horizontal carousel with manual arrow controls and
 * hover-to-pause. Uses requestAnimationFrame to advance a `translateX`
 * offset every frame, and wraps modulo the half-track width (the track
 * is rendered twice back-to-back) for a seamless loop.
 *
 * Users can:
 *   • hover the carousel → auto-scroll pauses so they can read/click
 *   • click ← / → arrows → step by one item width in either direction
 *
 * Props:
 *   speed        — px/second, default 40
 *   pauseOnHover — bool, default true
 *   gap          — px between items, default 16
 *   padY         — vertical padding to protect card shadows, default 24
 *   showArrows   — bool, default true
 *   itemClassName — applied to each item wrapper (e.g. `w-64 shrink-0`)
 */
export default function Marquee({
  speed = 40,
  pauseOnHover = true,
  gap = 16,
  padY = 24,
  showArrows = true,
  className = "",
  itemClassName = "",
  // Stable id used to persist the current offset in sessionStorage so the
  // carousel resumes where it left off after an SPA back-nav. Without this
  // the user sees a jarring ~100px snap-back every time they return to the
  // home screen from /servers.
  id = null,
  children,
}) {
  const storageKey = id ? `marquee-offset:${id}` : null;
  // Seed initial offset from sessionStorage synchronously so the first
  // paint already has the saved transform applied — no visible jump.
  const initialOffsetRef = useRef(
    (() => {
      if (!storageKey || typeof window === "undefined") return 0;
      const raw = window.sessionStorage.getItem(storageKey);
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })()
  );
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const offsetRef = useRef(initialOffsetRef.current); // current translateX in px (0..halfWidth)
  const halfWidthRef = useRef(0);     // width of one copy of the content
  const pausedRef = useRef(false);    // hover / focus pause
  const nudgingUntilRef = useRef(0);  // performance.now() when a manual nudge ends
  const [needsMarquee, setNeedsMarquee] = useState(true);
  const [hovered, setHovered] = useState(false);

  const items = useMemo(() => {
    const arr = Array.isArray(children) ? children : [children];
    return arr.filter(Boolean);
  }, [children]);

  // When children change (e.g. an async list finishes loading), always
  // start from the marquee branch so the measurement below has real
  // content to size against. If it turns out the row fits, we'll flip
  // to the fallback right after.
  useEffect(() => {
    setNeedsMarquee(true);
  }, [items.length]);

  // Measure — decide whether we even need to animate. Runs on children /
  // resize changes.
  const measure = useCallback(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const track = trackRef.current;
    if (!track) return;
    // Track is duplicated content, so halfWidth is one copy.
    const halfWidth = track.scrollWidth / 2;
    halfWidthRef.current = halfWidth;
    const containerWidth = wrap.offsetWidth || 0;
    const enough = halfWidth > containerWidth + 1;
    setNeedsMarquee(enough);
    if (!enough) {
      // Only hard-reset the offset if the track has REAL content that
      // genuinely fits inside the wrapper. If the track has no children,
      // we're in the "items still loading from API after SPA back-nav"
      // state — zeroing offsetRef here would wipe out the
      // sessionStorage-seeded value that lets us resume the carousel
      // from where it left off. Preserve offset in that case; measure()
      // runs again once items populate.
      if (track.children.length > 0) {
        offsetRef.current = 0;
        track.style.transform = "translateX(0)";
      }
      return;
    }
    // Apply the current offset synchronously after measuring. On remount
    // (e.g. React Router back-nav), offsetRef is seeded from sessionStorage
    // and the track's own transform is still "" because this is a fresh
    // DOM node — so without this call the user sees the saved offset only
    // on the first rAF frame instead of the first paint.
    if (halfWidth > 0) {
      const clamped = ((offsetRef.current % halfWidth) + halfWidth) % halfWidth;
      offsetRef.current = clamped;
      track.style.transform = `translateX(${-clamped}px)`;
    }
  }, []);

  useEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure());
    // Observe BOTH the wrapper (viewport width change) and the track
    // (content width change — e.g. async-loaded items or image swap).
    // Without observing the track, halfWidth goes stale and the wrap
    // point ends up mid-item, causing a visible "jump" every half-cycle.
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [items, measure]);

  // Animation loop. Advances offset by speed*dt when not paused. Manual
  // nudges use a temporary CSS transition; while a nudge is in-flight we
  // skip the rAF-driven translation so the two don't fight.
  //
  // Three subtle gotchas we guard against (all caused the carousel to
  // "stick then rubberband" in production):
  //   1. First frame after mount is often 300-800ms late because the
  //      browser is still painting on page refresh — `last` is seeded
  //      inside step() instead so the first dt is always ~0.
  //   2. When the tab is backgrounded, rAF pauses. Coming back, `t - last`
  //      can be minutes. We clamp dt to 100ms so a single frame can't
  //      wrap the carousel multiple times.
  //   3. On `visibilitychange` back to visible, we reset `last` so the
  //      very first post-wake frame doesn't still carry the gap forward.
  useEffect(() => {
    if (!needsMarquee) return undefined;
    let rafId = 0;
    let last = null; // seeded on first step() call
    const MAX_DT = 0.1; // seconds — cap any single frame's advance
    // Periodically persist the offset so SPA back-nav doesn't reset the
    // carousel to zero. Writing on every frame would be wasteful; every
    // 500ms is plenty for a smooth resume on return.
    let lastSave = 0;
    const SAVE_INTERVAL_MS = 500;

    const persist = () => {
      if (!storageKey || typeof window === "undefined") return;
      try {
        window.sessionStorage.setItem(storageKey, String(offsetRef.current));
      } catch {
        // sessionStorage can throw in private browsing / disabled storage —
        // non-fatal, just skip persistence.
      }
    };

    const onVisibility = () => {
      // Force the next frame to re-seed `last` so a backgrounded tab
      // doesn't blow through the whole half-track on return.
      if (!document.hidden) last = null;
      else persist();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const step = (t) => {
      if (last === null) last = t;
      const dt = Math.min((t - last) / 1000, MAX_DT);
      last = t;
      const track = trackRef.current;
      const half = halfWidthRef.current;
      const docHidden =
        typeof document !== "undefined" && document.hidden === true;
      if (
        track &&
        half > 0 &&
        !pausedRef.current &&
        !docHidden &&
        t >= nudgingUntilRef.current
      ) {
        let next = offsetRef.current + speed * dt;
        // JS `%` with negative LHS keeps the sign — clamp defensively so
        // offset is always in [0, half).
        next = ((next % half) + half) % half;
        offsetRef.current = next;
        track.style.transform = `translateX(${-next}px)`;
      }
      if (t - lastSave >= SAVE_INTERVAL_MS) {
        persist();
        lastSave = t;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("visibilitychange", onVisibility);
      // Final save on unmount so the next mount resumes exactly here.
      persist();
    };
  }, [needsMarquee, speed, storageKey]);

  const nudge = useCallback(
    (dir /* -1 or 1 */) => {
      const track = trackRef.current;
      const wrap = wrapperRef.current;
      if (!track || !wrap) return;
      const half = halfWidthRef.current;
      if (half <= 0) return;

      const firstItem = track.firstElementChild;
      const itemW = firstItem ? firstItem.offsetWidth : 200;
      const stride = itemW + gap;
      const containerW = wrap.offsetWidth || 320;

      // To CENTER item index `i` in the viewport, we need:
      //   translateX = -(i*stride + itemW/2 - containerW/2)
      // Equivalent offset in our sign convention:
      const centerOffsetFor = (i) => i * stride + itemW / 2 - containerW / 2;

      // Figure out which item is currently closest to the viewport center,
      // then step ±1 from there. Using round() means partial-drifted state
      // from autoplay always snaps cleanly to the nearest card.
      const currentIndex = Math.round(
        (offsetRef.current + containerW / 2 - itemW / 2) / stride
      );
      const nextIndex = currentIndex + dir;

      const rawNext = centerOffsetFor(nextIndex);
      // Wrap into [0, half) so we always animate a short distance.
      const wrapped = ((rawNext % half) + half) % half;

      // If wrapping causes rawNext to fall outside [0, half), we'd
      // otherwise animate the whole track. Instead JUMP by ±half with
      // the transition disabled so we're on the same side as `wrapped`,
      // then animate the small remaining distance.
      if (rawNext < 0 || rawNext >= half) {
        const preShift =
          rawNext < 0 ? offsetRef.current + half : offsetRef.current - half;
        track.style.transition = "none";
        track.style.transform = `translateX(${-preShift}px)`;
        void track.offsetWidth; // force reflow
      }

      offsetRef.current = wrapped;
      track.style.transition = "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)";
      track.style.transform = `translateX(${-wrapped}px)`;
      // Freeze the rAF loop for the transition duration so autoplay
      // doesn't stomp on the nudge mid-flight.
      nudgingUntilRef.current = performance.now() + 450;
      window.setTimeout(() => {
        if (trackRef.current) trackRef.current.style.transition = "";
      }, 450);
    },
    [gap]
  );

  useEffect(() => {
    pausedRef.current = pauseOnHover && hovered;
  }, [pauseOnHover, hovered]);

  // Fallback: short content — render a normal row.
  if (!needsMarquee) {
    return (
      <div
        ref={wrapperRef}
        className={`flex overflow-x-auto no-scrollbar ${className}`}
        style={{ gap, paddingTop: padY, paddingBottom: padY }}
      >
        {items.map((child, i) => (
          <div key={i} className={itemClassName}>
            {child}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={pauseOnHover ? () => setHovered(true) : undefined}
      onMouseLeave={pauseOnHover ? () => setHovered(false) : undefined}
      className={`relative overflow-hidden ${className}`}
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
        paddingTop: padY,
        paddingBottom: padY,
      }}
    >
      <div
        ref={trackRef}
        className="flex w-max will-change-transform"
        style={{
          gap,
          paddingRight: gap,
          // Seed the transform at first paint using the sessionStorage-restored
          // offset. Without this, there's a 1-frame flash of translateX(0)
          // between React mounting the node and the first rAF measuring
          // the half-width — visible as a brief snap-back on SPA back-nav.
          transform: `translateX(${-initialOffsetRef.current}px)`,
        }}
      >
        {items.map((child, i) => (
          <div key={`a-${i}`} className={itemClassName}>
            {child}
          </div>
        ))}
        {items.map((child, i) => (
          <div
            key={`b-${i}`}
            className={itemClassName}
            aria-hidden="true"
            // Prevent duplicate keyboard tab stops and duplicate links for
            // screen readers / SEO — this half is purely visual filler.
            // React 19 treats `inert=""` as false (logs a warning); the
            // boolean form is required for the attribute to be applied.
            inert={true}
          >
            {child}
          </div>
        ))}
      </div>

      {showArrows && (
        <>
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label="Previous"
            data-testid="marquee-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/70 hover:bg-black/90 border border-white/10 text-white flex items-center justify-center backdrop-blur transition-all active:scale-90"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label="Next"
            data-testid="marquee-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/70 hover:bg-black/90 border border-white/10 text-white flex items-center justify-center backdrop-blur transition-all active:scale-90"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
}
